import { ratingsListQueryOptions } from '@/api/queries/ratings';
import { Text } from '@/components/ui/text';
import { Rating } from '@/types/rating';
import { cn } from '@/lib/utils';
import { useAppTheme } from '@/providers/theme-provider';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';

export interface RatingButtonGroupProps {
  /** Currently selected rating ID */
  selectedRatingId: number | null;
  /** Callback when a rating is selected */
  onSelect: (ratingId: number, ratingKey: string) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
}

export const RATING_CARD_CONFIG: Record<
  string,
  { label: string; description: string; barColor: string; barCount: number }
> = {
  not_for_me: {
    label: 'Skip',
    description: "I wouldn't order this again.",
    barColor: '#585450',
    barCount: 1,
  },
  okay: {
    label: 'Okay',
    description: 'Fine. Nothing memorable.',
    barColor: '#A8A49C',
    barCount: 2,
  },
  try_it: {
    label: 'Try It',
    description: "I'd happily order this again.",
    barColor: '#9ACD32',
    barCount: 3,
  },
  must_try: {
    label: 'Must Try',
    description: 'The thing you tell a friend about.',
    barColor: '#D25E34',
    barCount: 4,
  },
};

const BAR_HEIGHTS = ['17%', '33%', '67%', '100%'];

const GRID_LAYOUT = [
  ['not_for_me', 'okay'],
  ['try_it', 'must_try'],
];

export function RatingButtonGroup({
  selectedRatingId,
  onSelect,
  disabled = false,
}: RatingButtonGroupProps) {
  const theme = useAppTheme();

  const { data: ratings, isLoading, error } = useQuery(ratingsListQueryOptions());

  const ratingsByKey = React.useMemo(() => {
    if (!ratings) return {};
    return Object.fromEntries(ratings.map((r: Rating) => [r.key, r]));
  }, [ratings]);

  if (isLoading) {
    return (
      <View className="py-6 items-center">
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  if (error || !ratings || ratings.length === 0) {
    return (
      <View className="py-6 items-center">
        <Text className="text-sm text-error">
          Unable to load ratings
        </Text>
      </View>
    );
  }

  return (
    <View className="py-2">
      <Text className="text-xl text-foreground mb-3">
        How was it?
      </Text>

      <View style={{ gap: 8 }}>
        {GRID_LAYOUT.map((row, rowIndex) => (
          <View key={rowIndex} className="flex-row" style={{ gap: 8 }}>
            {row.map((key) => {
              const rating = ratingsByKey[key];
              const config = RATING_CARD_CONFIG[key];
              if (!rating || !config) return null;

              const isSelected = selectedRatingId === rating.id;

              return (
                <Pressable
                  key={key}
                  onPress={() => onSelect(rating.id, rating.key)}
                  disabled={disabled}
                  className={cn(
                    'flex-1 rounded-xl p-3 border-2',
                    !isSelected && 'border-transparent bg-surface-secondary'
                  )}
                  style={{
                    ...(isSelected && {
                      backgroundColor: config.barColor + '18',
                      borderColor: config.barColor,
                    }),
                    borderCurve: 'continuous',
                    opacity: disabled ? 0.5 : 1,
                  }}
                  accessibilityLabel={`Rate as ${config.label}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected, disabled }}
                >
                  <View className="flex-row items-end h-6 mb-2">
                    {BAR_HEIGHTS.map((height, i) => (
                      <View
                        key={i}
                        className={cn(
                          'w-[5px] mx-[1px] rounded-sm',
                          i >= config.barCount && 'bg-secondary'
                        )}
                        style={{
                          height: height as any,
                          ...(i < config.barCount && {
                            backgroundColor: config.barColor,
                          }),
                        }}
                      />
                    ))}
                  </View>

                  <Text
                    className={cn(
                      'text-base font-bold',
                      isSelected ? '' : 'text-foreground'
                    )}
                    style={isSelected ? { color: config.barColor } : undefined}
                  >
                    {config.label}
                  </Text>

                  <Text className="text-xs text-muted-foreground mt-0.5" numberOfLines={2}>
                    {config.description}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}

export default RatingButtonGroup;
